import { gql } from '@apollo/client';

const MEAL_FRAGMENT = gql`
  fragment MealFragment on Meal {
    __typename

    _id
    coverImages {
      url
    }
    coverImageUrl
    status
    title
    desc
    instructions
    createdAt
    updatedAt
    tags
    allergies

    isUsingIngredientMacros

    prepTimeSeconds
    cookTimeSeconds
    totalTimeSeconds
    recipeYield

    macros {
      fatGrams
      carbGrams
      proteinGrams
      calories
    }

    recipeIngredients {
      _id
      name
      quantity
      quantityUnit
      macros {
        fatGrams
        carbGrams
        proteinGrams
        calories
      }
    }
    recipeInstructions {
      _id
      name
      text
      image {
        url
      }
    }

    webUrl
    mobilePreviewUrl
  }
`;

export const GET_MEALS = gql`
  query GetMeals($input: MealsQueryInput) {
    meals(input: $input) {
      ...MealFragment
    }
  }
  ${MEAL_FRAGMENT}
`;

export const GET_MEAL = gql`
  query GetMeal($id: ID!) {
    meal(id: $id) {
      ...MealFragment
    }
  }
  ${MEAL_FRAGMENT}
`;

export const CREATE_MEAL = gql`
  mutation CreateMeal($input: MealInput!) {
    createMeal(input: $input) {
      ...MealFragment
    }
  }
  ${MEAL_FRAGMENT}
`;

export const DUPLICATE_MEAL = gql`
  mutation DuplicateMeal($id: ID!) {
    duplicateMeal(id: $id) {
      ...MealFragment
    }
  }
  ${MEAL_FRAGMENT}
`;

export const DELETE_MEAL = gql`
  mutation DeleteMeal($id: ID!) {
    deleteMeal(id: $id) {
      message
    }
  }
`;

export const UPDATE_MEAL = gql`
  mutation UpdateMeal($id: ID!, $input: MealInput!) {
    updateMeal(id: $id, input: $input) {
      ...MealFragment
    }
  }
  ${MEAL_FRAGMENT}
`;

export const MEAL_PLAN_FRAGMENT = gql`
  fragment MealPlanFragment on MealPlan {
    __typename

    _id
    userId
    coverImages {
      url
    }
    coverImageUrl
    status
    title
    desc
    createdAt
    updatedAt
    startedAt
    tags
    events {
      _id
      mealId
      meal {
        ...MealFragment
      }
      day
      timeOfDay
    }
  }
  ${MEAL_FRAGMENT}
`;

export const GET_MEAL_PLANS = gql`
  query GetMealPlans($input: MealPlansQueryInput) {
    mealPlans(input: $input) {
      ...MealPlanFragment
    }
  }
  ${MEAL_PLAN_FRAGMENT}
`;

export const GET_MEAL_PLAN = gql`
  query GetMealPlan($id: ID!) {
    mealPlan(id: $id) {
      ...MealPlanFragment
    }
  }
  ${MEAL_PLAN_FRAGMENT}
`;

export const CREATE_MEAL_PLAN = gql`
  mutation CreateMealPlan($input: MealPlanInput!) {
    createMealPlan(input: $input) {
      ...MealPlanFragment
    }
  }
  ${MEAL_PLAN_FRAGMENT}
`;

export const DELETE_MEAL_PLAN = gql`
  mutation DeleteMealPlan($id: ID!) {
    deleteMealPlan(id: $id) {
      message
    }
  }
`;

export const UPDATE_MEAL_PLAN = gql`
  mutation UpdateMealPlan($id: ID!, $input: MealPlanInput!) {
    updateMealPlan(id: $id, input: $input) {
      ...MealPlanFragment
    }
  }
  ${MEAL_PLAN_FRAGMENT}
`;

const NUTRITION_PREFERENCES_FRAGMENT = gql`
  fragment NutritionPreferencesFragment on NutritionPreferences {
    __typename
    _id
    userId
    allergies
    snacksPerDay
    mealsPerDay
    dietType
    macroNutrientsPerDay {
      fatGrams
      carbGrams
      proteinGrams
      calories
    }
  }
`;

export const MEAL_PLAN_ASSIGNMENT_FRAGMENT = gql`
  fragment MealPlanAssignmentFragment on MealPlanAssignment {
    __typename
    _id
    userId
    trainerId
    mealPlanId
    status
    assignedAt
    startedAt
    completedAt
    createdAt
    updatedAt
    mealPlan {
      ...MealPlanFragment
    }
  }
  ${MEAL_PLAN_FRAGMENT}
`;

export const GET_MEAL_PLAN_ASSIGNMENTS = gql`
  query GetMealPlanAssignments($input: MealPlanAssignmentsQueryInput) {
    mealPlanAssignments(input: $input) {
      ...MealPlanAssignmentFragment
    }
  }
  ${MEAL_PLAN_ASSIGNMENT_FRAGMENT}
`;

export const GET_NUTRITION_PREFERENCES = gql`
  query GetNutritionPreferences($input: GetNutritionPreferencesInput) {
    getNutritionPreferences(input: $input) {
      ...NutritionPreferencesFragment
    }
  }
  ${NUTRITION_PREFERENCES_FRAGMENT}
`;

export const CALCULATE_MACRO_NUTRIENTS = gql`
  query CalculateMacroNutrients($input: MacroNutrientsInput) {
    calculateMacroNutrients(input: $input) {
      fatGrams
      carbGrams
      proteinGrams
      calories
    }
  }
`;

export const ASSIGN_MEAL_PLAN = gql`
  mutation AssignMealPlan($input: AssignMealPlanInput!) {
    assignMealPlan(input: $input) {
      ...MealPlanAssignmentFragment
    }
  }
  ${MEAL_PLAN_ASSIGNMENT_FRAGMENT}
`;

export const GENERATE_MEAL_PLAN_FOR_CLIENT = gql`
  mutation GenerateMealPlanForClient($input: GenerateMealPlanForClientInput!) {
    generateMealPlanForClient(input: $input) {
      ...MealPlanAssignmentFragment
    }
  }
  ${MEAL_PLAN_ASSIGNMENT_FRAGMENT}
`;

export const STOP_MEAL_PLAN_ASSIGNMENT = gql`
  mutation StopMealPlanAssignment($id: ID!) {
    stopMealPlanAssignment(id: $id) {
      ...MealPlanAssignmentFragment
    }
  }
  ${MEAL_PLAN_ASSIGNMENT_FRAGMENT}
`;

export const COMPLETE_MEAL_PLAN_ASSIGNMENT = gql`
  mutation CompleteMealPlanAssignment($id: ID!) {
    completeMealPlanAssignment(id: $id) {
      ...MealPlanAssignmentFragment
    }
  }
  ${MEAL_PLAN_ASSIGNMENT_FRAGMENT}
`;

export const DELETE_MEAL_PLAN_ASSIGNMENT = gql`
  mutation DeleteMealPlanAssignment($id: ID!) {
    deleteMealPlanAssignment(id: $id) {
      id
      message
    }
  }
`;
